import * as React from 'react';
import { styled } from '@glitz/react';
import { type TextFieldPropType, useCombineEventHandlers, useTextField } from '@avensia/excite-ui';
import { createThemeDecorator, isLightshop, isSvenssons, Theme, type ThemeType } from '../../theme';
import { createSlideHeightAnimationComponent, createTransitionDecorator } from '@avensia/excite-ui';
import {
  createFieldSizeDecorator,
  FieldSize,
  fieldXSpacing,
  placeholderTopPosition,
  placeholderYSpacing,
  useFieldSize,
  type FieldSizeType,
} from '../field-size';
import { AlertIcon } from 'svenssons/shared/icons';
import { black } from 'webapp/shared/sharedTheme';

export function useInputValue(initial: string | (() => string) = '') {
  const [value, setValue] = React.useState(initial);
  return [
    value,
    React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => setValue(e.currentTarget.value), []),
  ] as const;
}

const resetInputStyled = styled({
  appearance: 'none',
  padding: { y: 0 },
  border: { xy: { style: 'none' } },
  color: 'inherit',
  backgroundColor: 'transparent',
  ':focus': { outlineStyle: 'none' },
});

type InputPropType = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'size'> & {
  size?: FieldSizeType;
  as?: ThemeType;
  invalid?: boolean;
  keepBorder?: boolean; //Can be used to keep border when using transparent theme
};

export const Input = styled(
  React.forwardRef(
    (
      { type = 'text', as = Theme.White, size, invalid, keepBorder, ...restProps }: InputPropType,
      ref: React.Ref<HTMLInputElement>,
    ) => {
      const defaultSize = useFieldSize();
      const fieldSize = size ?? defaultSize;
      const dynamic = React.useMemo(
        () =>
          styled(
            resetInputStyled,
            createThemeDecorator(as),
            createBorderDecorator(invalid ? Theme.Negative : keepBorder ? Theme.White : as),
            createFieldSizeDecorator(fieldSize),
            createTransitionDecorator({ property: ['background', 'color', 'box-shadow'] }),
            {
              '::placeholder': {
                color: t => t.monochrome.text,
                opacity: 0.6,
              },
            },
            invalid && invalidDecorator,
          ),
        [as, fieldSize, invalid, keepBorder],
      );
      return <styled.Input type={type} {...restProps} css={dynamic} ref={ref} />;
    },
  ),
);

type FieldPropType = InputPropType & TextFieldPropType;

export type { FieldPropType as TextFieldPropType };

export const TextField = styled(
  React.forwardRef(
    (
      { children, error, validate, ...restProps }: React.PropsWithChildren<FieldPropType>,
      ref: React.Ref<HTMLInputElement>,
    ) => {
      const elementRef = React.useRef<HTMLInputElement>(null);
      React.useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => elementRef.current);
      const [minimizePlaceholder, setMinimizePlaceholder] = React.useState(
        elementRef?.current && elementRef.current.value?.length > 0,
      );
      const { invalid, validationMessage, inputProps } = useTextField(elementRef, { error, validate });

      const toggleLabel = React.useCallback(() => {
        const hideLabel = elementRef?.current && elementRef.current.value?.length > 0;
        if (minimizePlaceholder !== hideLabel) {
          setMinimizePlaceholder(hideLabel);
        }
      }, [minimizePlaceholder]);

      const onBlur = useCombineEventHandlers(restProps.onBlur, inputProps.onBlur);
      const onInvalid = useCombineEventHandlers(restProps.onInvalid, inputProps.onInvalid);
      const change = useCombineEventHandlers(restProps.onChange, inputProps.onChange);

      const onChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          toggleLabel();
          change(e);
        },
        [change, toggleLabel],
      );

      React.useEffect(() => {
        toggleLabel();
      }, [toggleLabel]);

      const dynamic = React.useMemo(
        () =>
          styled(
            {
              width: '100%',
              ':focus': {
                outline: { color: t => t.inputFocusOutlineColor, width: 1, style: 'solid' },
                border: { xy: { color: t => t.inputFocusOutlineColor } },
                boxShadow: 'none',
              },
            },
            createTransitionDecorator({
              property: ['padding'],
              duration: '0ms',
            }),
            !children &&
              Boolean(restProps.placeholder) &&
              Boolean(minimizePlaceholder) &&
              placeholderYSpacing(restProps.size ?? FieldSize.Default),
          ),
        [children, minimizePlaceholder, restProps.placeholder, restProps.size],
      );

      const placeholderDecorator = styled({
        top: placeholderTopPosition(restProps.size ?? FieldSize.Default),
        fontSize: isLightshop || isSvenssons ? 12 : 10,
        opacity: 0.6,
        left: 0,
        paddingLeft: fieldXSpacing(restProps.size ?? FieldSize.Default),
      });

      return (
        <Base>
          {children ? (
            <FieldLabel>{children}</FieldLabel>
          ) : (
            restProps.placeholder && (
              <PlaceholderLabel css={Boolean(minimizePlaceholder) && placeholderDecorator}>
                {restProps.placeholder}
              </PlaceholderLabel>
            )
          )}
          <Input
            {...restProps}
            {...inputProps}
            invalid={invalid}
            onChange={onChange}
            onBlur={onBlur}
            onInvalid={onInvalid}
            css={dynamic}
            ref={elementRef}
          />
          <ValidationMessage visible={invalid}>
            {isSvenssons && <Alert />}
            {validationMessage}
          </ValidationMessage>
        </Base>
      );
    },
  ),
);

export function createBorderDecorator(as: ThemeType) {
  if (isSvenssons) {
    switch (as) {
      case Theme.Monochrome:
        return styled({
          border: { xy: { color: t => t.inputFocusOutlineColor, style: 'solid', width: 'thin' } },
        });
      case Theme.White:
        return styled({
          border: { xy: { color: t => t.inputFocusOutlineColor, style: 'solid', width: 'thin' } },
        });
      default:
        return styled({ border: { xy: { width: 1, style: 'solid', color: t => t[as].color } } });
    }
  } else {
    return styled({
      border: { xy: { width: 1, style: 'solid', color: t => t[as].border } },
    });
  }
}

const Base = styled.label({
  position: 'relative',
});

const PlaceholderLabel = styled.span(
  {
    top: t => t.sharedSpacing.s,
    opacity: 0,
    color: t => t.monochrome.text,
    fontSize: t => t.fontSize.s,
    position: 'absolute',
  },
  createTransitionDecorator({
    property: ['background, font-size, top'],
    duration: '100ms',
    timingFunction: 'ease-in-out',
  }),
);

export const FieldLabel = styled.span({
  display: 'block',
  fontSize: t => t.fontSize.xxs,
  marginBottom: t => t.sharedSpacing.xs,
});

export const ValidationMessage = createSlideHeightAnimationComponent(
  styled.span({
    display: 'flex',
    gap: t => t.sharedSpacing.xxs,
    fontSize: t => t.fontSize.xxs,
    color: t => (isSvenssons ? black : t.negative.color),
    marginTop: t => t.sharedSpacing.xs,
    textAlign: 'left',
  }),
);

const Alert = styled(AlertIcon, {
  flexShrink: 0,
});

const invalidDecorator = styled({
  outlineStyle: 'solid',
  outlineColor: t => t.negative.color,
  outlineWidth: 1,
});
